import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { publicListClients, publicSubmitRequest } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft, Sparkles, Building2, Wind, Hammer, Cog } from "lucide-react";

export default function Request() {
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    client_id: "",
    site_id: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    category: "hvac",
    title: "",
    description: "",
    priority: "medium",
  });

  useEffect(() => {
    publicListClients().then((d) => {
      setClients(d.clients);
      setSites(d.sites);
    });
  }, []);

  const clientSites = sites.filter((s) => s.client_id === form.client_id);

  const submit = async () => {
    if (!form.client_id || !form.contact_name || !form.contact_email || !form.title) {
      toast.error("Please fill required fields");
      return;
    }
    setBusy(true);
    try {
      const out = await publicSubmitRequest(form);
      setDone(out);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border border-zinc-200 rounded-sm p-8 text-center">
          <div className="h-14 w-14 mx-auto rounded-sm bg-emerald-100 flex items-center justify-center mb-4">
            <CheckCircle2 className="h-7 w-7 text-emerald-700" />
          </div>
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-1">
            Request received
          </div>
          <h1 className="font-display font-extrabold text-3xl mb-2">Thank you, {form.contact_name.split(" ")[0]}.</h1>
          <p className="text-zinc-700 mb-4">
            Your request has been routed to <b>{done.client}</b>. A dispatcher will reach out shortly.
          </p>
          <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 font-mono text-sm">
            Reference: <b className="text-orange-600">{done.number}</b>
          </div>
          <Link
            to="/request"
            onClick={() => {
              setDone(null);
              setForm({
                client_id: "",
                site_id: "",
                contact_name: "",
                contact_email: "",
                contact_phone: "",
                category: "hvac",
                title: "",
                description: "",
                priority: "medium",
              });
            }}
            data-testid="request-another"
            className="inline-block mt-6 text-sm font-bold tracking-wider uppercase text-orange-600 hover:text-orange-700"
          >
            Submit another request →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-sm bg-orange-500 flex items-center justify-center font-display font-extrabold text-zinc-950">
              F
            </div>
            <div>
              <div className="font-display font-extrabold text-lg leading-none">FieldOps</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">
                Service Requests
              </div>
            </div>
          </Link>
          <Link
            to="/login"
            data-testid="back-to-login"
            className="text-xs font-bold tracking-wider uppercase text-zinc-600 hover:text-orange-600 flex items-center gap-1"
          >
            <ArrowLeft className="h-3 w-3" /> Sign in
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-10 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2">
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-orange-600 mb-2">
            Public service request
          </div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mb-3">
            Need a fix? Tell us what&apos;s broken.
          </h1>
          <p className="text-zinc-700 leading-relaxed mb-6">
            Submit a corrective maintenance request and a CM work order will land
            directly on the dispatcher&apos;s board &mdash; fully tracked, with SLA timers
            and AI-assisted triage.
          </p>
          <div className="space-y-3">
            <Bullet icon={Wind} text="HVAC failures · refrigerant, airflow, comfort" />
            <Bullet icon={Cog} text="Equipment breakdowns · pumps, motors, generators" />
            <Bullet icon={Hammer} text="Civil works · roofs, concrete, structural" />
            <Bullet icon={Sparkles} text="AI triage suggestions on every request" />
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="bg-white border border-zinc-200 rounded-sm p-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wider">
                  Service provider <span className="text-orange-600">*</span>
                </Label>
                <Select
                  value={form.client_id}
                  onValueChange={(v) => setForm({ ...form, client_id: v, site_id: "" })}
                >
                  <SelectTrigger data-testid="req-client" className="rounded-sm h-11">
                    <Building2 className="h-4 w-4 mr-1" />
                    <SelectValue placeholder="Select your service provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wider">Site / Location</Label>
                <Select
                  value={form.site_id}
                  onValueChange={(v) => setForm({ ...form, site_id: v })}
                  disabled={!form.client_id}
                >
                  <SelectTrigger data-testid="req-site" className="rounded-sm h-11">
                    <SelectValue placeholder="Optional — auto-select if blank" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientSites.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} {s.city ? `· ${s.city}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Field
                label="Your name *"
                testid="req-name"
                value={form.contact_name}
                onChange={(v) => setForm({ ...form, contact_name: v })}
              />
              <Field
                label="Email *"
                testid="req-email"
                type="email"
                value={form.contact_email}
                onChange={(v) => setForm({ ...form, contact_email: v })}
              />
              <Field
                label="Phone"
                testid="req-phone"
                value={form.contact_phone}
                onChange={(v) => setForm({ ...form, contact_phone: v })}
              />
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="req-category" className="rounded-sm h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hvac">HVAC</SelectItem>
                    <SelectItem value="equipment">Equipment</SelectItem>
                    <SelectItem value="civil">Civil Work</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field
                label="Short title *"
                testid="req-title"
                value={form.title}
                onChange={(v) => setForm({ ...form, title: v })}
                full
                placeholder="e.g. AC not cooling 4th floor"
              />
              <div className="col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wider">What happened?</Label>
                <Textarea
                  data-testid="req-desc"
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="rounded-sm"
                  placeholder="Symptoms, noises, when it started, severity…"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wider">Urgency</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="req-priority" className="rounded-sm h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low — within a week</SelectItem>
                    <SelectItem value="medium">Medium — within 24h</SelectItem>
                    <SelectItem value="high">High — within 8h</SelectItem>
                    <SelectItem value="critical">Critical — same day</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              data-testid="req-submit"
              disabled={busy}
              onClick={submit}
              className="mt-6 w-full h-12 bg-orange-500 hover:bg-orange-600 rounded-sm font-bold text-base"
            >
              {busy ? "Submitting…" : "Submit Service Request"}
            </Button>
            <p className="text-[11px] text-zinc-500 mt-3 text-center">
              By submitting you agree to be contacted about this request.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const Bullet = ({ icon: Icon, text }) => (
  <div className="flex items-start gap-3">
    <div className="h-8 w-8 rounded-sm bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
      <Icon className="h-4 w-4" />
    </div>
    <div className="text-sm text-zinc-700 pt-1.5">{text}</div>
  </div>
);

const Field = ({ label, value, onChange, testid, type = "text", full, placeholder }) => (
  <div className={full ? "col-span-2" : ""}>
    <Label className="text-xs font-semibold uppercase tracking-wider">{label}</Label>
    <Input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-sm h-11"
      placeholder={placeholder}
    />
  </div>
);
